import { showSuccess, showHttpError } from '@calendly/alert'
import { useQueryParams } from '@calendly/hooks'
import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import pick from 'lodash-es/pick'

import { setGetParams } from 'actions/core/navigation'
import { rootApi } from 'root-api'

import { setDeletedForm, setRenamedForm, setClonedForm, setIsExportPopupOpen } from './slice'

const api = rootApi.enhanceEndpoints({ addTagTypes: ['ROUTING_FORMS'] }).injectEndpoints({
  endpoints: (build) => ({
    fetchForms: build.query({
      query: (params) => ({
        url: '/api/form_builder/forms',
        params
      }),
      providesTags: ['ROUTING_FORMS'],
      extraOptions: {
        onSuccess: ({ collection, pagination }, { dispatch }) => {
          if (collection.length === 0 && pagination.currentPage > 1) dispatch(setGetParams({ page: '1' }))
        }
      }
    }),
    fetchInsight: build.query({
      query: () => ({ url: '/api/form_builder/forms/insight' })
    }),
    deleteForm: build.mutation({
      query: ({ id }) => ({
        url: `/api/form_builder/forms/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ROUTING_FORMS'],
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(setDeletedForm(null))
        }
      }
    }),
    renameForm: build.mutation({
      query: ({ id, name }) => ({
        url: `/api/form_builder/forms/${id}`,
        method: 'PATCH',
        data: { name }
      }),
      invalidatesTags: ['ROUTING_FORMS'],
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(setRenamedForm(null))
          dispatch(showSuccess(I18n.t('publisher.flash.changes_saved')))
        }
      }
    }),
    cloneForm: build.mutation({
      query: ({ id, values }) => ({
        url: `/api/form_builder/forms/${id}/clone`,
        method: 'POST',
        data: values
      }),
      invalidatesTags: ['ROUTING_FORMS'],
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(setClonedForm(null))
        }
      }
    }),
    exportForm: build.mutation({
      query: ({ id, ...params }) => ({
        url: `/api/form_builder/forms/${id}/export`,
        params,
        method: 'POST'
      }),
      onQueryStarted: async ({ id, ...params }, { dispatch, queryFulfilled }) => {
        try {
          const { data: { location } } = await queryFulfilled

          if (location === 'inline') {
            window.open(`/api/form_builder/forms/${id}/export${buildQuery(params, { addQueryPrefix: true })}`, '_self')
          } else {
            dispatch(setIsExportPopupOpen(true))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
  })
})

export const useFetchFormsQuery = () => {
  const queryParams = useQueryParams()
  const pageNumber = Number(queryParams.page || 1)
  const pageSize = Number(queryParams.size || 20)

  const query = {
    pageNumber,
    pageSize,
    ...pick(queryParams, ['sort', ['last_response_at', 'response_count']])
  }

  return api.useFetchFormsQuery(query)
}

export const {
  useDeleteFormMutation,
  useRenameFormMutation,
  useCloneFormMutation,
  useExportFormMutation,
  useFetchInsightQuery
} = api
